import { useState, useCallback, useEffect, useRef } from 'react'

/**
 * Hook for copying text to the clipboard with an auto-resetting "copied" indicator.
 *
 * @param {Object} [options]
 * @param {number} [options.resetDelay=2000] - Milliseconds before `isCopied` resets to false
 * @returns {{ isCopied: boolean, copyToClipboard: (text: string) => void, reset: () => void }}
 */
const useCopyToClipboard = (options = {}) => {
    const { resetDelay = 2000 } = options
    const [isCopied, setIsCopied] = useState(false)
    const timerRef = useRef(null)

    const reset = useCallback(() => {
        setIsCopied(false)
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
    }, [])

    const copyToClipboard = useCallback(
        text => {
            navigator.clipboard.writeText(text)
            setIsCopied(true)

            if (timerRef.current) clearTimeout(timerRef.current)
            timerRef.current = setTimeout(() => {
                setIsCopied(false)
                timerRef.current = null
            }, resetDelay)
        },
        [resetDelay],
    )

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current)
        }
    }, [])

    return { isCopied, copyToClipboard, reset }
}

export default useCopyToClipboard
